
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  date: string;
}

const Blog = () => {
  const { toast } = useToast();
  
  const { data: posts, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['blog-posts'],
    queryFn: async () => {
      console.log("Fetching blog posts...");
      try {
        const { data, error } = await supabase
          .from('blog_posts')
          .select('*')
          .order('date', { ascending: false });
        
        if (error) {
          console.error("Supabase query error:", error);
          throw error;
        }
        
        console.log("Fetched blog posts:", data?.length || 0);
        return data as BlogPost[];
      } catch (err) {
        console.error("Error in blog posts fetch:", err);
        throw err;
      }
    },
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
    staleTime: 0,
  });

  const handleRetry = () => {
    toast({
      title: "Retrying...",
      description: "Attempting to fetch blog posts again."
    });
    refetch();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-blue-50 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <Link to="/">
            <Button variant="default" className="mb-4 bg-blue-600 hover:bg-blue-700">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Timer
            </Button>
          </Link>
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-4xl font-bold tracking-tight bg-gradient-to-r from-gray-900 via-blue-800 to-gray-900 bg-clip-text text-transparent mb-2">
                Blog
              </h1>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/blog/new">
                <Button className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700">
                  <Plus className="h-4 w-4" />
                  New Blog
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {isLoading && (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="p-6">
                <Skeleton className="h-8 w-3/4 mb-2" />
                <Skeleton className="h-4 w-1/4 mb-3" />
                <Skeleton className="h-16 w-full mb-4" />
                <Skeleton className="h-10 w-28" />
              </Card>
            ))}
          </div>
        )}

        {isError && (
          <Card className="p-6 border-red-200 bg-red-50">
            <h3 className="text-lg font-medium text-red-800 mb-2">Unable to load blog posts</h3>
            <p className="text-red-600 mb-4">
              We're having trouble connecting to the server. This might be due to network issues or server maintenance.
            </p>
            <Button onClick={handleRetry} variant="outline">
              Retry
            </Button>
          </Card>
        )}

        {!isLoading && !isError && (
          <div className="grid gap-6">
            {posts?.length ? (
              posts.map((post) => (
                <Card key={post.id} className="p-6 hover:shadow-lg transition-shadow">
                  <h2 className="text-2xl font-semibold mb-2">{post.title}</h2>
                  <p className="text-sm text-gray-500 mb-3">
                    {new Date(post.date).toLocaleDateString()}
                  </p>
                  <p className="text-gray-600 mb-4">{post.excerpt}</p>
                  <Link to={`/blog/${post.id}`}>
                    <Button variant="outline">Read More</Button>
                  </Link>
                </Card>
              ))
            ) : (
              <Card className="p-6 text-center">
                <p className="text-gray-500">No blog posts available.</p>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Blog;
